import React, { useContext, useState } from "react";
import styles from "./DraftEnquiries.module.css";
import AutoComplete from "../../Shared/AutosuggestV2/Autosuggestv2";
import Modal from "../../Shared/PopupModal/PopupModal";
import ShortlistLisp from "../NewEnquiry/ShortlistLsp";
import CreatableSelect from "react-select/creatable";
import MultiSelectWithCheckbox from "../../Shared/MultiSelectCheckbox/MultiSelectCheckbox";
import { UserApiContext } from "../../UserDetailsContext/UserDetailsContext";
import { getContractTenureOptions } from "../../../Utility";

const selectStyles = {
  control: (base, state) => ({
    ...base,
    borderRadius: "6px",
    border: `1px solid ${state.isFocused ? "#393185" : "#e2e8f0"}`,
    minHeight: "36px",
    height: "36px",
    fontSize: "13px",
    boxShadow: "none",
    "&:hover": { border: "1px solid #393185" },
  }),
  valueContainer: (base) => ({ ...base, padding: "0 10px", height: "36px" }),
  placeholder: (base) => ({ ...base, color: "#94a3b8" }),
  indicatorsContainer: (base) => ({ ...base, height: "36px" }),
};

const formatDate = (dateStr) => {
  if (!dateStr) return "N/A";
  const d = new Date(dateStr);
  if (isNaN(d)) return "N/A";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const DraftDetail = ({
  draft,
  formData,
  onChange,
  onBack,
  onSave,
  onAccept,
  onReject,
  truckOptions = [],
  productOptions = [],
}) => {
  const [saving, setSaving] = useState(false);
  const { userDetails } = useContext(UserApiContext)
  const tenureOptions = getContractTenureOptions(userDetails?.policy?.adhoc);
  const missing = [];
  if (!formData.origin) missing.push("origin");
  if (!formData.destination) missing.push("destination");
  if (!formData.truck_types?.value) missing.push("truck type");
  if (!formData.due_date) missing.push("placement date");

  const isComplete = missing.length === 0;

  const lspCount =
    formData.benchmark_transporter_list?.filter((t) => t.isChecked === true)
      ?.length || 0;
  const lane = draft?.enquiry_lane_details?.lane_details;
  const submissionDate = formatDate(draft?.created_at);

  const setsend_data_wrapper = (newData) => {
    const updatedItem = newData[0];
    if (updatedItem?.benchmark_transporter_list) {
      onChange(
        "benchmark_transporter_list",
        updatedItem.benchmark_transporter_list,
      );
    }
  };

  const handleSaveClick = async () => {
    setSaving(true);
    await onSave();
    setSaving(false);
  };

  const renderAutoComplete = (field, label, placeholder, required) => {
    const currentVal = formData[field];
    const displayVal =
      currentVal?.location_name ||
      (typeof currentVal === "string" ? currentVal : "");
    const hasError = required && !displayVal;

    return (
      <div className={styles.formGroup}>
        <label className={styles.label}>
          {label}
          {required && <span className={styles.required}>*</span>}
        </label>
        <AutoComplete
          value={displayVal ? { value: displayVal, label: displayVal } : ""}
          placeholder={placeholder}
          module_key="lorri_spot"
          user_identifier={userDetails?.profile_company_id}
          onChange={(val) => onChange(field, val)}
          onSelect={(obj) => onChange(field, obj)}
          customStyle={{
            control: (base) => ({
              ...base,
              borderRadius: "6px",
              border: `1px solid ${hasError ? "#fca5a5" : "#e2e8f0"}`,
              padding: "0 2px",
              fontSize: "13px",
              minHeight: "36px",
              height: "36px",
              boxShadow: "none",
              "&:hover": { border: "1px solid #393185" },
            }),
            indicatorsContainer: (base) => ({ ...base, height: "36px" }),
            valueContainer: (base) => ({
              ...base,
              height: "36px",
              padding: "0 10px",
            }),
          }}
        />
        {hasError && (
          <span className={styles.hint}>
            <i className="fa fa-exclamation-circle"></i> Required
          </span>
        )}
      </div>
    );
  };

  return (
    <div className={styles.detailContainer}>
      {/* Top action bar */}
      <div className={styles.detailTopBar}>
        <button className={styles.backBtn} onClick={onBack}>
          <i className="fa fa-chevron-left"></i> Back to Drafts
        </button>

        <div className={styles.topBarCenter}>
          {isComplete ? (
            <span className={styles.badgeReady}>
              <i className="fa fa-check-circle"></i> Ready to Accept
            </span>
          ) : (
            <span className={styles.badgeIncomplete}>
              <i className="fa fa-exclamation-circle"></i> {missing.length}{" "}
              field{missing.length > 1 ? "s" : ""} missing
            </span>
          )}
        </div>

        <div className={styles.topBarActions}>
          <button
            className={`${styles.btn} ${styles.btnPrimaryDetail}`}
            onClick={handleSaveClick}
            disabled={saving}
          >
            <i className="fa fa-save"></i> {saving ? "Saving..." : "Save"}
          </button>
          <button
            className={`${styles.btn} ${isComplete ? styles.btnAcceptDetail : styles.btnDisabled}`}
            onClick={() => isComplete && onAccept()}
            disabled={!isComplete}
            title={!isComplete ? `Missing: ${missing.join(", ")}` : ""}
          >
            <i className="fa fa-check"></i> Accept
          </button>
          <button
            className={`${styles.btn} ${styles.btnRejectDetail}`}
            onClick={() => onReject(draft.draft_id)}
          >
            <i className="fa fa-trash-o"></i> Reject
          </button>
        </div>
      </div>

      <div className={styles.detailBody}>
        {/* Left sidebar */}
        <div className={styles.sidePanel}>
          {/* AI Reasoning */}
          {draft?.reasoning && missing?.length > 0 && (
            <div className={styles.reasoningBox}>
              <div className={styles.reasoningTitle}>
                <i className="fa fa-lightbulb-o"></i> AI Reasoning
              </div>
              <p className={styles.reasoningText}>{draft.reasoning}</p>
            </div>
          )}

          {/* Missing fields */}
          {missing.length > 0 && (
            <div className={styles.missingSection}>
              <div className={styles.missingSectionTitle}>
                <i className="fa fa-exclamation-triangle"></i> Missing Fields
              </div>
              <div className={styles.missingTags}>
                {missing.map((f) => (
                  <span key={f} className={styles.missingTag}>
                    {f}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Metadata */}
          <div className={styles.metaSection}>
            <div className={styles.metaRow}>
              <i className="fa fa-calendar-o"></i>
              <span>{submissionDate}</span>
            </div>
            {lspCount > 0 && (
              <div className={styles.metaRow}>
                <i className="fa fa-users"></i>
                <span>
                  {lspCount} shortlisted transporter{lspCount > 1 ? "s" : ""}
                </span>
              </div>
            )}
            {lane?.tonnage && (
              <div className={styles.metaRow}>
                <i className="fa fa-balance-scale"></i>
                <span>{lane.tonnage} MT</span>
              </div>
            )}
          </div>
        </div>

        {/* Right form panel */}
        <div className={styles.formPanel}>
          {/* Lane Details */}
          <div className={styles.formSection}>
            <div className={styles.formSectionTitle}>Lane Details</div>
            <div className={styles.formGrid}>
              {renderAutoComplete(
                "origin",
                "Origin",
                "Enter pickup location",
                true,
              )}
              {renderAutoComplete(
                "destination",
                "Destination",
                "Enter drop location",
                true,
              )}
            </div>
          </div>

          {/* Truck & Schedule */}
          <div className={styles.formSection}>
            <div className={styles.formSectionTitle}>Truck & Schedule</div>
            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <label className={styles.label}>
                  Truck Type<span className={styles.required}>*</span>
                </label>
                <CreatableSelect
                  options={truckOptions}
                  value={(() => {
                    const flattened = truckOptions.flatMap(
                      (g) => g.options || [g],
                    );
                    return (
                      flattened.find(
                        (opt) => opt.value === formData.truck_types?.value,
                      ) ||
                      (formData.truck_types?.value
                        ? {
                          label:
                            formData.truck_types.label ||
                            formData.truck_types.value,
                          value: formData.truck_types.value,
                        }
                        : null)
                    );
                  })()}
                  onChange={(opt) =>
                    onChange(
                      "truck_types",
                      opt
                        ? {
                          label: opt.value,
                          value: opt.value,
                          truck_types_group: opt.truck_types_group || "",
                          isMyTruck: opt.isMyTruck || false,
                        }
                        : {},
                    )
                  }
                  placeholder="Select or type truck type..."
                  styles={{
                    ...selectStyles,
                    control: (base, state) => ({
                      ...selectStyles.control(base, state),
                      border: `1px solid ${!formData.truck_types?.value ? "#fca5a5" : state.isFocused ? "#393185" : "#e2e8f0"}`,
                    }),
                  }}
                />
                {!formData.truck_types?.value && (
                  <span className={styles.hint}>
                    <i className="fa fa-exclamation-circle"></i> Required
                  </span>
                )}
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Product Type</label>
                <MultiSelectWithCheckbox
                  options={productOptions}
                  onChange={(val) => onChange("product_types", val)}
                  value={formData.product_types || []}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>
                  Placement Date<span className={styles.required}>*</span>
                </label>
                <input
                  className={`${styles.input} ${!formData.due_date ? styles.inputErr : ""}`}
                  type="date"
                  min={new Date(new Date().getTime() - new Date().getTimezoneOffset() * 60000).toISOString().split('T')[0]}
                  value={formData.due_date || ""}
                  onChange={(e) => onChange("due_date", e.target.value)}
                />
                {!formData.due_date && (
                  <span className={styles.hint}>
                    <i className="fa fa-exclamation-circle"></i> Required
                  </span>
                )}
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Contract Tenure</label>
                <select
                  className={styles.input}
                  value={
                    (Array.isArray(formData.contract_tenure)
                      ? formData.contract_tenure[0]?.value
                      : formData.contract_tenure?.value ||
                      formData.contract_tenure) || "Spot Rates"
                  }
                  onChange={(e) =>
                    onChange("contract_tenure", {
                      label: e.target.value,
                      value: e.target.value,
                    })
                  }
                >
                  {tenureOptions.map((opt) => (
                    <option
                      key={opt.value}
                      value={opt.value}
                      disabled={opt.isDisabled}
                    >
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Load & Capacity */}
          <div className={styles.formSection}>
            <div className={styles.formSectionTitle}>Load & Capacity</div>
            <div className={styles.threeColRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Weight</label>
                <div className={styles.weightInputGroup}>
                  <input
                    className={styles.weightInput}
                    placeholder="Weight"
                    value={formData.capacity || ""}
                    onChange={(e) => onChange("capacity", e.target.value)}
                  />
                  <div className={styles.unitToggle}>
                    <button
                      className={`${styles.unitBtn} ${formData.capacity_unit !== "KG" ? styles.unitBtnActive : ""}`}
                      onClick={() => onChange("capacity_unit", "MT")}
                      type="button"
                    >
                      MT
                    </button>
                    <button
                      className={`${styles.unitBtn} ${formData.capacity_unit === "KG" ? styles.unitBtnActive : ""}`}
                      onClick={() => onChange("capacity_unit", "KG")}
                      type="button"
                    >
                      KG
                    </button>
                  </div>
                </div>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>No. of Trucks</label>
                <input
                  className={styles.input}
                  type="number"
                  min="1"
                  value={formData.truck_required || ""}
                  onChange={(e) => onChange("truck_required", e.target.value)}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Shortlisted Transporters</label>
                <Modal
                  buttonLabel={
                    lspCount > 0
                      ? `${lspCount} LSP${lspCount > 1 ? "s" : ""} selected`
                      : "Select Transporters"
                  }
                  buttonClass="btn btn-link"
                  content={
                    <ShortlistLisp
                      item={formData}
                      setsend_data={setsend_data_wrapper}
                      send_data={[formData]}
                      idx={0}
                    />
                  }
                  title="Shortlist Transporters"
                  subtitle="(This enquiry will be sent to the selected transporters)"
                  id="closebutton"
                  activityName={`LSP Clicked for ${formData?.origin?.location_name} to ${formData?.destination?.location_name}`}
                  backdrop={false}
                  style={{
                    fontSize: "13px",
                    padding: "0 2px",
                    height: "36px",
                    textDecoration: "underline",
                    color: "#393185",
                    textAlign: "left",
                    width: "fit-content",
                  }}
                  size="md"
                />
              </div>
            </div>
          </div>
          {/* Additional Notes */}
          <div className={styles.formSection}>
            <div className={styles.formSectionTitle}>Additional Notes</div>
            <div className={styles.formGroup}>
              <textarea
                className={styles.textarea}
                rows={3}
                placeholder="Any extra details about this shipment..."
                value={formData.additional_details || ""}
                onChange={(e) => onChange("additional_details", e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DraftDetail;
